import { useEffect, useState } from 'react'
import { SortableTable } from '@/components/SortableTable'
import { DetailRow } from '@/components/DetailRow'
import { DiskSpaceSnapshotCard } from '@/components/DiskSpaceSnapshotCard'
import { TrafficLightSummary } from '@/components/TrafficLightSummary'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { fetchPostureThresholds } from '@/api/posture'
import { formatDateTime, formatRam } from '@/lib/formatAgent'
import {
  filterOtherHardwareComponents,
  findHardwareByName,
  formatCpuSummaryLine,
  formatHardwareDetails,
  formatHardwareManufacturer,
} from '@/lib/formatHardware'
import { evaluateHardwarePosture } from '@/lib/hardwareTrafficLight'
import { DEFAULT_POSTURE_THRESHOLDS, loadPostureThresholds } from '@/lib/postureThresholds'

function inventoryIdentityValue(row) {
  if (!row) return null
  const manufacturer = formatHardwareManufacturer(row)
  const details = formatHardwareDetails(row)
  const parts = [manufacturer !== '—' ? manufacturer : null, details !== '—' ? details : null].filter(
    Boolean,
  )
  return parts.length ? parts.join(' · ') : null
}

function SectionHeading({ children }) {
  return (
    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  )
}

export function AgentHardware({ agent, hardware = [], snapshots = [], loading }) {
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [thresholds, setThresholds] = useState(DEFAULT_POSTURE_THRESHOLDS)

  useEffect(() => {
    let cancelled = false
    loadPostureThresholds(fetchPostureThresholds).then((data) => {
      if (!cancelled) setThresholds(data)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const posture = evaluateHardwarePosture({
    agent,
    hardware,
    snapshots,
    thresholds,
    exclusions: agent?.posture_exclusions,
  })
  const diskSnapshot = snapshots.find((item) => item.script_slug === 'disk-space')

  const cpuLine = formatCpuSummaryLine(agent)
  const systemRow = findHardwareByName(hardware, 'System')
  const biosRow = findHardwareByName(hardware, 'BIOS')
  const macRow = findHardwareByName(hardware, 'MAC addresses')
  const systemValue = inventoryIdentityValue(systemRow)
  const biosValue = inventoryIdentityValue(biosRow)
  const macValue = macRow ? formatHardwareDetails(macRow) : null
  const hasIdentity = Boolean(
    (systemValue && systemValue !== '—') ||
      (biosValue && biosValue !== '—') ||
      (macValue && macValue !== '—'),
  )

  const otherComponents = filterOtherHardwareComponents(hardware, agent.system_drive)

  const summaryBits = [
    cpuLine,
    agent.ram_mb ? formatRam(agent.ram_mb) : null,
    agent.machine_type ? String(agent.machine_type).replace(/_/g, ' ') : null,
  ].filter(Boolean)

  const collapsedSummary = [
    summaryBits.join(' · ') || null,
    otherComponents.length > 0
      ? `${otherComponents.length} other component${otherComponents.length === 1 ? '' : 's'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="space-y-6">
      <TrafficLightSummary title="Hardware at a glance" posture={posture} />

      <CollapsePanel
        id="hardware-details"
        title="Hardware"
        collapsedSummary={collapsedSummary || undefined}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        bodyClassName="space-y-5"
      >
        <div className="space-y-2 text-sm">
          <SectionHeading>Key specs</SectionHeading>
          <DetailRow label="CPU" value={cpuLine} />
          <DetailRow label="RAM" value={agent.ram_mb ? formatRam(agent.ram_mb) : null} />
          <DetailRow
            label="Type"
            value={agent.machine_type ? String(agent.machine_type).replace(/_/g, ' ') : null}
          />
          <DetailRow label="System drive" value={agent.system_drive} />
          <DetailRow label="Windows serial" value={agent.windows_serial_number} />
          <DetailRow label="Agent version" value={agent.agent_version} />
          <DetailRow label="Last seen" value={formatDateTime(agent.last_seen)} />
        </div>

        {hasIdentity && (
          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <SectionHeading>Identity</SectionHeading>
            {systemValue && systemValue !== '—' && (
              <DetailRow label="System" value={systemValue} />
            )}
            {biosValue && biosValue !== '—' && <DetailRow label="BIOS" value={biosValue} />}
            {macValue && macValue !== '—' && (
              <div className="flex justify-between gap-4">
                <span className="shrink-0 text-muted-foreground">MAC addresses</span>
                <span className="max-w-[70%] break-all text-right font-medium">{macValue}</span>
              </div>
            )}
          </div>
        )}

        {(loading || otherComponents.length > 0) && (
          <div className="border-t border-border pt-4">
            <SectionHeading>Other components</SectionHeading>
            <SortableTable
              loading={loading}
              empty="No additional hardware components recorded."
              data={otherComponents}
              defaultSort={{ id: 'name', dir: 'asc' }}
              columns={[
                {
                  id: 'name',
                  label: 'Name',
                  sortValue: (h) => h.name?.toLowerCase() ?? '',
                  render: (h) => h.name || '—',
                },
                {
                  id: 'manufacturer',
                  label: 'Manufacturer',
                  sortValue: (h) => formatHardwareManufacturer(h).toLowerCase(),
                  render: (h) => formatHardwareManufacturer(h),
                },
                {
                  id: 'details',
                  label: 'Details',
                  sortValue: (h) => formatHardwareDetails(h).toLowerCase(),
                  render: (h) => {
                    const text = formatHardwareDetails(h)
                    return (
                      <span className="block max-w-md whitespace-normal break-words" title={text}>
                        {text}
                      </span>
                    )
                  },
                },
              ]}
            />
          </div>
        )}
      </CollapsePanel>

      {diskSnapshot ? (
        <DiskSpaceSnapshotCard snapshot={diskSnapshot} thresholds={thresholds} />
      ) : (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No disk space data yet. Deploy the disk-space RMM script from Atera to populate free
            space and largest folders.
          </CardContent>
        </Card>
      )}
    </div>
  )
}
